import { useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import BusinessIcon from '@mui/icons-material/Business';
import Collapse from '@mui/material/Collapse';
import { css } from '@emotion/react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { BooleanFilter } from '@e-socna/utils';
import type {
  FragmentType,
  GenericStatusId,
  RepresentativeTypeId,
  UserRoleId,
} from '@e-socna/data-layer';

import { PATH_ORGANIZATION_DEFAULT } from '../../app/constants/paths.constant';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { N_A } from '../../constants/labels.constant';
import {
  formatOrganizationIdentifierValue,
  getOrganizationIdentifierLabel,
} from '../../utils/format-organization-identifier.util';

import { ExpandMoreCard } from '../ExpandMoreCard';
import { GenericStatusIndicator } from '../GenericStatusIndicator';
import { Avatar } from '../Avatar';
import { thirdPartyTooltipOf } from '../../utils/third-party-tooltip.util';

export const PersonMembershipCardCmp_PersonMembership_Fragment = graphql(`
  fragment PersonMembershipCardCmp_PersonMembership_Fragment on PersonMembership {
    organization {
      _id
      color
      logoImageUri
      name
      identifier {
        kind
        value
      }
      statusId
      tradingName
    }
    functions
    contextualRoleIds
    representativeTypeIds
  }
`);

interface Props {
  readonly membership: FragmentType<
    typeof PersonMembershipCardCmp_PersonMembership_Fragment
  >;
  readonly action?: ReactNode;
}

export const PersonMembershipCard = ({
  membership: pMembership,
  action,
}: Readonly<Props>) => {
  const { representativeTypesIdMap, userRolesIdMap } = useReferencesContext();

  const { organization, functions, contextualRoleIds, representativeTypeIds } =
    getFragmentData(
      PersonMembershipCardCmp_PersonMembership_Fragment,
      pMembership,
    );

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const path = useBuildPath(PATH_ORGANIZATION_DEFAULT, {
    id: organization._id,
  });

  const roles = contextualRoleIds
    ?.map((roleId) => userRolesIdMap[roleId as UserRoleId]?.label)
    .filter(BooleanFilter) ?? ['Inconnu'];

  const representativeTypes = representativeTypeIds
    ?.map(
      (representativeTypeId) =>
        representativeTypesIdMap[representativeTypeId as RepresentativeTypeId]
          ?.label,
    )
    .filter(BooleanFilter) ?? ['Inconnu'];

  return (
    <Grid
      size={{
        lg: 3,
        md: 6,
        xs: 12,
      }}
      sx={{ flexGrow: 1 }}
    >
      <Card>
        <CardHeader
          action={
            <GenericStatusIndicator
              statusId={organization.statusId as GenericStatusId}
              variant="icon"
            />
          }
          avatar={
            <Avatar
              bgColor={organization.color}
              icon={<BusinessIcon />}
              imageUri={organization.logoImageUri}
              label={thirdPartyTooltipOf(organization.name)}
            />
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
          subheader={getOrganizationIdentifierLabel(organization.identifier)}
          title={
            formatOrganizationIdentifierValue(organization.identifier) ??
            'Inconnu'
          }
        />
        <CardContent>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {organization.name}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Raison Sociale
          </Typography>
          <Typography gutterBottom variant="body2">
            Fonction(s) occupée(s) :
            <br />
            {functions.length ? functions.slice().sort().join(', ') : N_A}
          </Typography>
          <Typography gutterBottom variant="body2">
            Rôle(s) e-SOCNA :
            <br />
            {roles.length ? roles.slice().sort().join(', ') : N_A}
          </Typography>
          <Typography gutterBottom variant="body2">
            Type(s) de représentant :
            <br />
            {representativeTypes.length
              ? representativeTypes.slice().sort().join(', ')
              : N_A}
          </Typography>
        </CardContent>
        <CardActions disableSpacing>
          <Button component={Link} size="small" to={path} variant="outlined">
            Consulter
          </Button>
          {action}
          <ExpandMoreCard
            expanded={expanded}
            onExpandClick={handleExpandClick}
          />
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <Typography gutterBottom variant="overline">
              Nom commercial
            </Typography>
            <Typography gutterBottom variant="body2">
              {organization.tradingName ?? 'Non renseigné'}
            </Typography>
          </CardContent>
        </Collapse>
      </Card>
    </Grid>
  );
};
